import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  className?: string;
  aspectRatio?: string;
  showSkeleton?: boolean;
  priority?: boolean;
  /** Approximate rendered width in CSS pixels; used to request a right-sized image. */
  sizes?: string;
}

/**
 * Rewrite a Supabase Storage public URL to use the on-the-fly image
 * transformation endpoint. Returns the original URL for non-Supabase hosts.
 */
const transformSupabaseImage = (url: string, width: number, quality = 75): string => {
  if (!url || !url.includes('/storage/v1/object/public/')) return url;
  try {
    const u = new URL(url);
    u.pathname = u.pathname.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/');
    u.searchParams.set('width', String(width));
    u.searchParams.set('quality', String(quality));
    u.searchParams.set('resize', 'contain');
    return u.toString();
  } catch {
    return url;
  }
};

const WIDTHS = [240, 360, 480, 720, 960, 1280];

const buildSrcSet = (url: string): string | undefined => {
  if (!url || !url.includes('/storage/v1/object/public/')) return undefined;
  return WIDTHS.map((w) => `${transformSupabaseImage(url, w)} ${w}w`).join(', ');
};

export const OptimizedImage = ({ 
  src, 
  alt, 
  className,
  aspectRatio = '3/4',
  showSkeleton = true,
  priority = false,
  sizes = '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw',
  ...props 
}: OptimizedImageProps) => {
  const [isLoaded, setIsLoaded] = useState(priority);
  const [isInView, setIsInView] = useState(priority);
  const [error, setError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (!imgRef.current || priority) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before image enters viewport
      }
    );

    observer.observe(imgRef.current);

    return () => observer.disconnect();
  }, [priority]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setError(true);
    setIsLoaded(true);
  };

  // Default to a 720px-wide variant — good for most card/detail layouts on
  // mobile and desktop. Browsers pick the right one from srcSet.
  const transformedSrc = error ? '/placeholder.svg' : transformSupabaseImage(src, 720);
  const srcSet = error ? undefined : buildSrcSet(src);

  return (
    <div 
      ref={imgRef}
      className={cn("relative overflow-hidden", className)}
      style={{ aspectRatio }}
    >
      {/* Skeleton Loader */}
      {showSkeleton && !isLoaded && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}
      
      {/* Actual Image */}
      {isInView && (
        <img
          src={transformedSrc}
          srcSet={srcSet}
          sizes={srcSet ? sizes : undefined}
          alt={alt}
          className={cn(
            "w-full h-full object-cover transition-opacity duration-300",
            isLoaded ? 'opacity-100' : 'opacity-0'
          )}
          onLoad={handleLoad}
          onError={handleError}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          {...{ fetchpriority: priority ? 'high' : 'auto' } as Record<string, string>}
          {...props}
        />
      )}
    </div>
  );
};
